import { graphql } from 'client/gql';
import { GroupType } from 'client/gql/graphql';
import { useGroupsTable } from 'components/Groups/hooks/useGroupsTable';
import { GroupEditorsModal } from 'components/Groups/modals/GroupEditorsModal';
import { GroupModal } from 'components/Groups/modals/GroupModal';
import { GroupSettingsModal } from 'components/Groups/modals/GroupSettingsModal';
import { GroupViewersModal } from 'components/Groups/modals/GroupViewersModal';
import { GroupsStudentTable } from 'components/Groups/tables/GroupsStudentTable';
import { GroupsTeacherTable } from 'components/Groups/tables/GroupsTeacherTable';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { showSuccessDeleteToast, showUserDbErrorToast } from 'utils/toasts';
import { BpPage } from '../../components/BpPage/BpPage';
import { OrganizationConfigContext } from '../../context/OrganizationConfigContext';
import { useAuthClaims } from '../../hooks/useAuthClaims';

const DeleteGroupMutationDocument = graphql(`
  mutation BpDeleteGroupFromGroups($id: ID!) {
    deleteBpGroup(uuid: $id) {
      entitiesDeleted
    }
  }
`);

const GroupsPageDataDocument = graphql(`
  query GroupsPageData(
    $groupWhere: GroupWhere!
    $calendarEventWhere: CalendarEventWhere!
    $organizationUuid: ID!
    $groupUuid: ID!
  ) {
    ...UseGroupsTableFields
    ...GroupEditorsFormFields
    ...GroupViewersFormFields
  }
`);

export const GroupsPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pimAuthClaims, updateClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organization = pimAuthClaims.getOrganization();
  const { currentSchoolYear, schoolyears } = useContext(OrganizationConfigContext);

  const { ConfirmationDialog, confirmDelete } = useGroupsTable();

  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [selectedYearUuid, setSelectedYearUuid] = useState<string>(currentSchoolYear.uuid);
  const [showSpanning, setShowSpanning] = useState<boolean>(true);
  const [claimsTrigger, setClaimsTrigger] = useState<boolean>(false);
  const [groupUuid, setGroupUuid] = useState<string | null>(null);
  const [groupModalOpen, setGroupModalOpen] = useState<boolean>(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
  const [editorsModalOpen, setEditorsModalOpen] = useState<boolean>(false);
  const [viewersModalOpen, setViewersModalOpen] = useState<boolean>(false);

  const [, deleteGroup] = useMutation(DeleteGroupMutationDocument);

  const [{ data, error }] = useQuery({
    query: GroupsPageDataDocument,
    variables: {
      organizationUuid: organization.uuid,
      schoolYearUuid: selectedYearUuid,
      groupUuid: groupUuid ?? '',
      groupWhere: {
        type: GroupType.Group,
        organization: { uuid_IN: pimAuthClaims.getAllowedOrganizationUuids() },
        AND: [
          {
            OR: [{ schoolYear: { uuid: selectedYearUuid } }, { schoolYear: showSpanning ? null : undefined }],
          },
          {
            OR: [
              { editors_SOME: { uuid: profileUuid } },
              { viewers_SOME: { uuid: profileUuid } },
              { admins_SOME: { uuid: profileUuid } },
            ],
          },
        ],
      },
      calendarEventWhere: {
        holderConnection: {
          Group: {
            node: {
              organization: { uuid_IN: pimAuthClaims.getAllowedOrganizationUuids() },
              type: GroupType.Group,
              AND: [
                {
                  OR: [{ schoolYear: { uuid: selectedYearUuid } }, { schoolYear: null }],
                },
                {
                  OR: [{ editors_SOME: { uuid: profileUuid } }, { viewers_SOME: { uuid: profileUuid } }],
                },
              ],
            },
          },
        },
        virtualLocations_SOME: {
          running: true,
        },
      },
    },
  });

  if (error) showUserDbErrorToast();

  const permissions = usePermissionChecker();

  async function handleDelete(uuid: string, name: string) {
    const result = await confirmDelete({
      title: t('groups.delete.title'),
      message: t('groups.delete.message', { name }),
      confirmText: t('groups.delete.confirmText'),
    });
    if (!result) return;
    setTableLoading(true);
    const { error } = await deleteGroup({ id: uuid });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
    }
    setTableLoading(false);
  }

  function handleSettings(uuid: string) {
    setGroupUuid(uuid);
    setSettingsModalOpen(true);
  }

  function handleToggleSpanning() {
    setShowSpanning(!showSpanning);
  }

  function handleAdd() {
    setGroupUuid(null);
    setGroupModalOpen(true);
  }

  function handleEdit(uuid: string) {
    setGroupUuid(uuid);
    setGroupModalOpen(true);
  }

  async function handleClose(withClaims: boolean) {
    setGroupModalOpen(false);
    setSettingsModalOpen(false);
    setEditorsModalOpen(false);
    setViewersModalOpen(false);
    setGroupUuid(null);
    if (withClaims) {
      await updateClaims(() => setClaimsTrigger(!claimsTrigger));
    }
  }

  return (
    <BpPage
      title={t('groups.myGroups', {
        year: schoolyears?.find((y) => y.uuid === selectedYearUuid)?.name ?? currentSchoolYear?.name,
      })}
      isForbidden={false}
      organization={pimAuthClaims.getOrganization()}
    >
      {permissions?.canCreateGroup(organization) ? (
        <GroupsTeacherTable
          query={data}
          permissions={permissions}
          loading={tableLoading}
          yearUuid={selectedYearUuid}
          showSpanning={showSpanning}
          onYearSelect={setSelectedYearUuid}
          onToggleSpanning={handleToggleSpanning}
          onNavigate={(uuid) => navigate('/groups/' + uuid)}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onSettings={(uuid) => handleSettings(uuid)}
        />
      ) : (
        <GroupsStudentTable
          query={data}
          yearUuid={selectedYearUuid}
          showSpanning={showSpanning}
          onYearSelect={setSelectedYearUuid}
          onToggleSpanning={handleToggleSpanning}
          onNavigate={(uuid) => navigate('/groups/' + uuid)}
        />
      )}

      <GroupModal
        isOpen={groupModalOpen}
        groupUuid={groupUuid}
        schoolYearUuid={selectedYearUuid}
        page='groups'
        onClose={() => handleClose(true)}
      />

      {groupUuid && (
        <GroupEditorsModal
          isOpen={editorsModalOpen}
          groupUuid={groupUuid}
          schoolYearUuid={selectedYearUuid}
          onClose={() => handleClose(true)}
          query={data}
        />
      )}

      {groupUuid && (
        <GroupViewersModal
          isOpen={viewersModalOpen}
          groupUuid={groupUuid}
          schoolYearUuid={selectedYearUuid}
          onClose={() => handleClose(true)}
          query={data}
        />
      )}

      {groupUuid && (
        <GroupSettingsModal isOpen={settingsModalOpen} groupUuid={groupUuid} onClose={() => handleClose(false)} />
      )}

      <ConfirmationDialog />
    </BpPage>
  );
};
